import moment from "moment";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { Card as RNECard, ListItem } from "react-native-elements";
import { getCurrentChallenge } from "../backend/challenges/Challenges";
import {
  getMarkdownTextForCraftableItem,
  getMarkdownTextForSpell,
} from "../backend/events/campus/LevelUp";
import { getFloorsAvailableForCreatures } from "../backend/exploration/Creatures";
import { DungeonFloor } from "../backend/exploration/dungeons/DungeonFloor";
import { Item } from "../backend/items/Item";
import { getCraftableItems } from "../backend/items/Items";
import {
  getNewGamePlusBuffLevel,
  getNewGamePlusCount,
  getNewGamePlusDifficultyLevel,
} from "../backend/NewGamePlus";
import { PlayerContext } from "../backend/PlayerContext";
import { getResearcherExpPerSecForSchools } from "../backend/research/Researchers";
import { SecretsByType, SecretType } from "../backend/secrets/Secrets";
import {
  getMaxPrimarySchoolLevel,
  getMaxSchoolLevel,
  getSchoolExp,
  getSchoolLevel,
  getTotalExpRequiredForLevel,
  getUnlockedSchools,
  maximumSchoolLevelProductionMultiplierDx,
  maximumSchoolLevelResearchMultiplier,
  School,
} from "../backend/spells/ElementsAndSchools";
import { Spell } from "../backend/spells/Spell";
import { getVisibleSpellsForSchool } from "../backend/spells/Spells";
import { getSortedStorylines } from "../backend/storylines/Storylines";
import { clone } from "../utils/CoreUtils";
import { formatNumber, formatTimeLong } from "../utils/FormattingUtils";
import { Card } from "./utility/Card";
import Divider from "./utility/Divider";
import { Markdown } from "./utility/Markdown";
import SecretsContainer from "./utility/SecretsContainer";
import StorylinesContainer from "./utility/StorylinesContainer";
import { Text } from "./utility/Text";

export function StatsSingleSchool(props: {
  school: School;
  isCompact: boolean;
}) {
  const school = props.school;
  const playerContext = useContext(PlayerContext);
  const allSchoolLevels = getUnlockedSchools(playerContext).map((eachSchool) =>
    getSchoolLevel(playerContext, eachSchool),
  );

  const newSpellsText = useMemo(() => {
    const thisLevelSpells = getVisibleSpellsForSchool(playerContext, school);
    const thisLevelCrafts = getCraftableItems(playerContext);
    const thisLevelFloors = getFloorsAvailableForCreatures(playerContext);
    const currentSchoolLevel = getSchoolLevel(playerContext, school);
    let nextUnlockLevelAdd = 0;
    let foundNewSpellsOrItems = false;
    let extraSpells: Spell[] = [];
    let extraCrafts: Item[] = [];
    let extraFloors: DungeonFloor[] = [];
    const fakeState = clone(playerContext);
    fakeState.isFake = true;
    while (
      !foundNewSpellsOrItems &&
      currentSchoolLevel + nextUnlockLevelAdd < getMaxSchoolLevel(playerContext)
    ) {
      nextUnlockLevelAdd += 1;
      fakeState.schoolExperience[school] = getTotalExpRequiredForLevel(
        fakeState,
        getSchoolLevel(playerContext, school) + nextUnlockLevelAdd,
        school,
      );
      const nextLevelSpells = getVisibleSpellsForSchool(fakeState, school);
      const nextLevelCrafts = getCraftableItems(fakeState);
      const nextLevelFloors = getFloorsAvailableForCreatures(fakeState);
      extraSpells = nextLevelSpells.filter(
        (spell) => !thisLevelSpells.includes(spell),
      );
      extraCrafts = nextLevelCrafts.filter(
        (item) => !thisLevelCrafts.includes(item),
      );
      extraFloors = nextLevelFloors.filter(
        (floor) => !thisLevelFloors.includes(floor),
      );
      if (
        extraSpells.length > 0 ||
        extraCrafts.length > 0 ||
        extraFloors.length > 0
      ) {
        foundNewSpellsOrItems = true;
      }
    }
    const newSpells = extraSpells.map(getMarkdownTextForSpell);
    const newCrafts = extraCrafts.map(
      getMarkdownTextForCraftableItem.bind(null, playerContext),
    );
    const newFloors = extraFloors.map((floor) => `*${floor.getFloorName()}*`);
    const newUnlocks = [...newSpells, ...newCrafts, ...newFloors];

    return newUnlocks.length > 0
      ? `Your next unlock is at level ${
          nextUnlockLevelAdd + currentSchoolLevel
        }:
    
${newUnlocks.join("\n")}`
      : `If you only level up this school, you will not unlock anything new!`;
  }, [JSON.stringify(allSchoolLevels)]);

  const nextLevelRequiredExp =
    getTotalExpRequiredForLevel(
      playerContext,
      getSchoolLevel(playerContext, school) + 1,
      school,
    ) - getSchoolExp(playerContext, school);

  const researchEstimate = getResearcherExpPerSecForSchools(playerContext);

  if (props.isCompact) {
    return (
      <View>
        <View style={styles.row}>
          <Text>To Next Level:</Text>
          <Text style={styles.valueText}>
            {formatNumber(nextLevelRequiredExp)} (
            {(researchEstimate?.[school as School] ?? 0) == 0 ||
            nextLevelRequiredExp == Infinity
              ? "∞"
              : formatTimeLong(
                  moment.duration(
                    (1000 * nextLevelRequiredExp) /
                      (researchEstimate?.[school as School] ?? 0),
                  ),
                )}
            )
          </Text>
        </View>
        <View>
          <Markdown>{newSpellsText}</Markdown>
        </View>
      </View>
    );
  } else {
    return (
      <View>
        <View style={styles.row}>
          <Text>Level:</Text>
          <Text style={styles.valueText}>
            {getSchoolLevel(playerContext, school)}
          </Text>
        </View>
        <View style={styles.row}>
          <Text>To Next Level:</Text>
          <Text style={styles.valueText}>
            {formatNumber(nextLevelRequiredExp)}
          </Text>
        </View>
        <View style={styles.row}>
          <Text>To Next Level (Time):</Text>
          <Text style={styles.valueText}>
            {(researchEstimate?.[school as School] ?? 0) == 0 ||
            nextLevelRequiredExp == Infinity
              ? "∞"
              : formatTimeLong(
                  moment.duration(
                    (1000 * nextLevelRequiredExp) /
                      (researchEstimate?.[school as School] ?? 0),
                  ),
                )}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={{ flex: 1 }}>
            Maximum Primary Level in previous runs (MPL):
          </Text>
          <Text style={styles.valueText}>
            {formatNumber(getMaxPrimarySchoolLevel(playerContext, school))}
          </Text>
        </View>
        <View style={styles.insideBlock}>
          <Markdown>{newSpellsText}</Markdown>
        </View>
      </View>
    );
  }
}

const StatsSchoolViewerSchool = React.memo(
  (props: {
    school: School;
    isExpanded: boolean;
    onPress: (school: School) => void;
  }) => {
    const playerContext = useContext(PlayerContext);

    const school = props.school;
    const title =
      `${school} Lv${getSchoolLevel(playerContext, school)}` +
      (school == playerContext.primarySchool ? " (Primary)" : "");

    const onPress = useCallback(
      () => props.onPress(school),
      [props.onPress, school],
    );
    return useMemo(
      () => (
        <View key={school}>
          <ListItem topDivider accessibilityRole="button" onPress={onPress}>
            <ListItem.Content>
              <ListItem.Title>{title}</ListItem.Title>
            </ListItem.Content>
            <ListItem.Chevron
              type="material"
              name={props.isExpanded ? "close" : "chevron-right"}
            />
          </ListItem>
          {props.isExpanded && (
            <View style={styles.schoolElementContainer}>
              <StatsSingleSchool school={school} isCompact={false} />
            </View>
          )}
        </View>
      ),
      [onPress, title, props.isExpanded, school],
    );
  },
);

function StatsSchoolViewer() {
  const [activeSection, setActiveSection] = useState("");
  const playerContext = useContext(PlayerContext);

  const schools = getUnlockedSchools(playerContext);

  const perSchool = schools.map((school, index) => {
    return (
      <StatsSchoolViewerSchool
        key={school}
        school={school}
        isExpanded={school === activeSection}
        onPress={setActiveSection}
      />
    );
  });

  return (
    <View>
      {perSchool}
      <Divider />
    </View>
  );
}

function StorylineViewer() {
  const playerContext = useContext(PlayerContext);
  const allStorylines = getSortedStorylines();
  const completedStorylines = allStorylines.filter((storyline) =>
    storyline.isCompleted(playerContext),
  );

  return useMemo(
    () => (
      <View>
        <View style={styles.row}>
          <Text>Total Completed Storylines:</Text>
          <Text style={styles.valueText}>
            {completedStorylines.length} / {allStorylines.length}
          </Text>
        </View>
        <View style={styles.row}>
          <Text>Completion Percentage:</Text>
          <Text style={styles.valueText}>
            {formatNumber(
              (100 * completedStorylines.length) / allStorylines.length,
            ) + "%"}
          </Text>
        </View>
        <View style={styles.storylineContainer}>
          <StorylinesContainer />
        </View>
      </View>
    ),
    [completedStorylines.length, allStorylines.length],
  );
}

function StatsOtherViewer() {
  const playerContext = useContext(PlayerContext);
  const totalDuration = moment.duration(
    playerContext.global?.secondsPlayed,
    "seconds",
  );
  const thisRunDuration = moment.duration(
    playerContext.thisRun?.secondsPlayed,
    "seconds",
  );
  const thisWorldDuration = moment.duration(
    playerContext.global?.thisWorldSecondsPlayed,
    "seconds",
  );
  const totalActiveDuration =
    playerContext.global?.activeSecondsPlayed == "untracked"
      ? null
      : formatTimeLong(
          moment.duration(playerContext.global?.activeSecondsPlayed, "seconds"),
        );
  const thisRunActiveDuration =
    playerContext.thisRun?.activeSecondsPlayed == "untracked"
      ? null
      : formatTimeLong(
          moment.duration(
            playerContext.thisRun?.activeSecondsPlayed,
            "seconds",
          ),
        );
  const thisWorldActiveDuration = formatTimeLong(
    moment.duration(
      playerContext.global.thisWorldActiveSecondsPlayed,
      "seconds",
    ),
  );
  const totalRealWorldDuration = moment.duration(
    Date.now() - playerContext.global?.startTimestamp ?? 0,
  );
  const thisRunRealWorldDuration = moment.duration(
    Date.now() - playerContext.thisRun?.startTimestamp ?? 0,
  );
  const thisWorldRealWorldDuration = moment.duration(
    Date.now() - playerContext.global.thisWorldStartTimestamp ?? 0,
  );

  const newGamePlusCount = getNewGamePlusCount(playerContext);
  const worldBoonLevel = getNewGamePlusBuffLevel(playerContext);
  const worldDifficultyLevel = getNewGamePlusDifficultyLevel(playerContext);

  const researchMultiplier =
    maximumSchoolLevelResearchMultiplier(playerContext);
  const productionMultiplier =
    maximumSchoolLevelProductionMultiplierDx(playerContext);

  const currentChallenge = getCurrentChallenge(playerContext);

  const primarySchoolAndElement = `${playerContext.primarySchool} - ${playerContext.primaryElement}`;

  const currentChallengeRow = useMemo(
    () =>
      currentChallenge && (
        <View style={styles.row}>
          <Text style={styles.captionText} numberOfLines={2}>
            Current challenge:
          </Text>
          <Text style={[styles.valueText, styles.valueTextLong]}>
            {currentChallenge.getName()}
          </Text>
        </View>
      ),
    [currentChallenge],
  );

  const primarySchoolAndElementRow = useMemo(
    () => (
      <View style={styles.row}>
        <Text style={styles.captionText} numberOfLines={2}>
          Primary school and element:
        </Text>
        <Text style={[styles.valueText, styles.valueTextLong]}>
          {primarySchoolAndElement}
        </Text>
      </View>
    ),
    [primarySchoolAndElement],
  );

  const inGameTimeTotalRow = useMemo(
    () => (
      <View style={styles.row}>
        <Text style={styles.captionText} numberOfLines={2}>
          In-game time played (total):
        </Text>
        <Text
          style={[styles.valueText, styles.valueTextLong]}
          numberOfLines={3}
        >
          {formatTimeLong(totalDuration)}
        </Text>
      </View>
    ),
    [formatTimeLong(totalDuration)],
  );

  const inGameTimeThisWorldRow = useMemo(
    () =>
      newGamePlusCount > 0 && (
        <View style={styles.row}>
          <Text style={styles.captionText} numberOfLines={3}>
            In-game time played (this world):
          </Text>
          <Text style={[styles.valueText, styles.valueTextLong]}>
            {formatTimeLong(thisWorldDuration)}
          </Text>
        </View>
      ),
    [newGamePlusCount, formatTimeLong(thisWorldDuration)],
  );

  const inGameTimeThisRunRow = useMemo(
    () => (
      <View style={styles.row}>
        <Text style={styles.captionText} numberOfLines={3}>
          In-game time played (this run):
        </Text>
        <Text style={[styles.valueText, styles.valueTextLong]}>
          {formatTimeLong(thisRunDuration)}
        </Text>
      </View>
    ),
    [formatTimeLong(thisRunDuration)],
  );

  const activeDurationTotalRow = useMemo(
    () =>
      totalActiveDuration != null && (
        <View style={styles.row}>
          <Text style={styles.captionText} numberOfLines={3}>
            Real world time played (total):
          </Text>
          <Text style={[styles.valueText, styles.valueTextLong]}>
            {totalActiveDuration}
          </Text>
        </View>
      ),
    [totalActiveDuration],
  );

  const activeDurationThisWorldRow = useMemo(
    () =>
      newGamePlusCount > 0 && (
        <View style={styles.row}>
          <Text style={styles.captionText} numberOfLines={3}>
            Real world time played (this world):
          </Text>
          <Text style={[styles.valueText, styles.valueTextLong]}>
            {thisWorldActiveDuration}
          </Text>
        </View>
      ),
    [newGamePlusCount, thisWorldActiveDuration],
  );

  const activeDurationThisRunRow = useMemo(
    () =>
      thisRunActiveDuration != null && (
        <View style={styles.row}>
          <Text style={styles.captionText} numberOfLines={3}>
            Real world time played (this run):
          </Text>
          <Text style={[styles.valueText, styles.valueTextLong]}>
            {thisRunActiveDuration}
          </Text>
        </View>
      ),
    [thisRunActiveDuration],
  );

  const realWorldDurationTotalRow = useMemo(
    () => (
      <View style={styles.row}>
        <Text style={styles.captionText} numberOfLines={3}>
          Real world time since starting the game:
        </Text>
        <Text style={[styles.valueText, styles.valueTextLong]}>
          {formatTimeLong(totalRealWorldDuration)}
        </Text>
      </View>
    ),
    [formatTimeLong(totalRealWorldDuration)],
  );

  const realWorldDurationThisWorldRow = useMemo(
    () =>
      newGamePlusCount > 0 && (
        <View style={styles.row}>
          <Text style={styles.captionText} numberOfLines={3}>
            Real world time since starting this world:
          </Text>
          <Text style={[styles.valueText, styles.valueTextLong]}>
            {formatTimeLong(thisWorldRealWorldDuration)}
          </Text>
        </View>
      ),
    [newGamePlusCount, formatTimeLong(thisWorldRealWorldDuration)],
  );

  const realWorldDurationThisRunRow = useMemo(
    () => (
      <View style={styles.row}>
        <Text style={styles.captionText} numberOfLines={3}>
          Real world time since starting this run:
        </Text>
        <Text style={[styles.valueText, styles.valueTextLong]}>
          {formatTimeLong(thisRunRealWorldDuration)}
        </Text>
      </View>
    ),
    [formatTimeLong(thisRunRealWorldDuration)],
  );

  const newGamePlusRows = useMemo(
    () =>
      newGamePlusCount > 0 && (
        <>
          <View style={styles.row}>
            <Text style={styles.captionText} numberOfLines={2}>
              Total times world created:
            </Text>
            <Text style={[styles.valueText, styles.valueTextLong]}>
              {formatNumber(newGamePlusCount)}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.captionText} numberOfLines={2}>
              World modifiers:
            </Text>
            <Text style={[styles.valueText, styles.valueTextLong]}>
              Boon {formatNumber(worldBoonLevel)} / Diff{" "}
              {formatNumber(worldDifficultyLevel)}
            </Text>
          </View>
        </>
      ),
    [newGamePlusCount, worldBoonLevel, worldDifficultyLevel],
  );

  const retirementRows = useMemo(
    () => (
      <>
        <View style={styles.row}>
          <Text>Total times retired:</Text>
          <Text style={styles.valueText}>
            {playerContext.global.totalTimesResetted}
          </Text>
        </View>
        <View style={styles.row}>
          <Text>Global research multiplier:</Text>
          <Text style={styles.valueText}>
            {formatNumber(researchMultiplier, { showDecimals: true })}x
          </Text>
        </View>
        <View style={styles.row}>
          <Text>Global production multiplier:</Text>
          <Text style={styles.valueText}>
            {formatNumber(productionMultiplier, { showDecimals: true })}x
          </Text>
        </View>
      </>
    ),
    [
      playerContext.global.totalTimesResetted,
      researchMultiplier,
      productionMultiplier,
    ],
  );

  return useMemo(
    () => (
      <View>
        {currentChallengeRow}
        {primarySchoolAndElementRow}
        {inGameTimeTotalRow}
        {inGameTimeThisWorldRow}
        {inGameTimeThisRunRow}
        {activeDurationTotalRow}
        {activeDurationThisWorldRow}
        {activeDurationThisRunRow}
        {realWorldDurationTotalRow}
        {realWorldDurationThisWorldRow}
        {realWorldDurationThisRunRow}
        {newGamePlusRows}
        {retirementRows}
      </View>
    ),
    [
      currentChallengeRow,
      primarySchoolAndElementRow,
      inGameTimeTotalRow,
      inGameTimeThisWorldRow,
      inGameTimeThisRunRow,
      activeDurationTotalRow,
      activeDurationThisWorldRow,
      activeDurationThisRunRow,
      realWorldDurationTotalRow,
      realWorldDurationThisWorldRow,
      realWorldDurationThisRunRow,
      newGamePlusRows,
      retirementRows,
    ],
  );
}

export default function Stats() {
  const playerContext = useContext(PlayerContext);
  const hasSecrets =
    SecretsByType.Basic.getAll().filter((secret) =>
      secret.isUnlocked(playerContext),
    ).length > 0;

  return useMemo(
    () => (
      <View style={styles.container}>
        <Card>
          <RNECard.Title accessibilityRole="header">Stats</RNECard.Title>
          <StatsOtherViewer />
        </Card>
        <Card>
          <RNECard.Title accessibilityRole="header">Schools</RNECard.Title>
          <StatsSchoolViewer />
        </Card>
        <Card>
          <RNECard.Title accessibilityRole="header">Storylines</RNECard.Title>
          <StorylineViewer />
        </Card>
        {hasSecrets && (
          <Card>
            <RNECard.Title accessibilityRole="header">Secrets</RNECard.Title>
            <SecretsContainer secretType={SecretType.Basic} />
          </Card>
        )}
      </View>
    ),
    [hasSecrets],
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
  },
  schoolElementContainer: {
    margin: 8,
    marginBottom: 24,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  valueText: {
    minWidth: 60,
    textAlign: "right",
    marginLeft: 8,
  },
  valueTextLong: {
    minWidth: 112,
  },
  storylineContainer: {
    marginTop: 8,
  },
  captionText: {
    flex: 1,
  },
  insideBlock: {
    marginTop: 16,
  },
});
